/**
 * What each figure on the Result page means, for somebody who has not met it before.
 *
 * Presentation copy, so it lives here rather than travelling with the data: the figures themselves
 * are the API's (decision 5.1), but what to call a standard deviation in front of a marketing
 * manager is a question about this page. Rewording it needs no deploy of anything but the site.
 *
 * Two rules held to throughout. Every sentence describes what was actually computed - "the share
 * whose final post was positive" and not "how many people liked it" - because a reader who acts on
 * a friendlier paraphrase acts on the wrong number. And where a figure is easy to misread, the
 * misreading is named: a mean stance of zero is the one people get wrong, every time.
 */

/** Keyed by the metric ids in `app/domain/insights/reactions.py`. */
export const TILE_HELP: Record<string, string> = {
  positive_share:
    "The share of personas whose final post was positive or strongly positive. Personas who " +
    "stayed neutral are not counted here, so this and the negative share do not add up to 100%.",
  negative_share:
    "The share whose final post was negative or strongly negative.",
  backlash_share:
    "The share at the very bottom of the scale. These are the personas most likely to complain " +
    "out loud, so it is watched separately from the negative share it is part of.",
  mean_stance:
    "The average of every persona's final post, from −2 strongly negative to +2 strongly " +
    "positive. Zero means the positives and negatives cancelled out, which is not the same as " +
    "everyone being neutral — Polarisation tells those two apart.",
  polarisation:
    "How spread out the reactions are. Near zero, the audience landed in much the same place. " +
    "Above about 1, opinion is split towards the two ends rather than bunched in the middle.",
};

/** Keyed by the panel titles on the page, so the heading and its explanation cannot drift apart. */
export const PANEL_HELP: Record<string, string> = {
  "Stance by round":
    "Where the audience sat after each round. Round 1 is each persona's own first reaction, " +
    "before it has seen anybody else. Later rounds come after they have read each other's posts, " +
    "so a band growing or shrinking between rounds is what the conversation did.",
  "Opinion shift":
    "The average move between a persona's first post and its last. +0.5 means the audience " +
    "warmed by half a point on the five-point scale. The counts underneath matter: a small " +
    "average can hide a lot of movement in both directions.",
  "What drove reactions":
    "Sentences from your message that personas quoted word for word when saying why they reacted " +
    "as they did, each persona counted once per sentence. The colours show where those personas " +
    "ended up, so a sentence quoted mostly by negative ones is where to look.",
  Themes:
    "The reasons personas gave, grouped into recurring themes by one extra model call after the " +
    "run. A summary rather than a measurement — the quotes under each theme are the part to check.",
  Posts:
    "Every persona's final post, the reasons it gave for it, and the parts of your message it " +
    "quoted. This is the raw material behind every figure above.",
};

/** The interval printed under every tile. Dynamic, because how wide it is depends on the audience. */
export function intervalHelp(personas: number): string {
  const many = personas === 1 ? "1 persona" : `${personas} personas`;
  return (
    `The range underneath is where this figure would probably land if the run were repeated with ` +
    `a different ${many}. It is wide because the audience is small.`
  );
}
